import { useState } from "react";

import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, matchPath } from "react-router-dom";

import { RAYCHAT_DASHBOARD_URL } from "utils/constants";
import styles from "./main-header.module.scss";
import classes from "./roadmap.module.scss";
import useTranslation from "hooks/useTranslation";
import { ExitIconComponent, RoadmapIcon } from "../../assets/icons";
import Modal from "components/Modal";
import ModalBody from "components/Modal/ModalBody";
import ModalFooter from "components/Modal/ModalFooter";
import { fetchUserRoadmap, logoutSocket } from "redux/chats/chats.actions";
import filledLockIcon from "assets/icons/icon-lock-fill.svg";
import { ForwardToOperator } from "components/Conversations";
import AcceptRejectConv from "components/Conversations/AcceptRejectConv";
import useMediaQuery from "use-mediaquery";
import RoadmapModal from "components/UserRoadmap/RoadmapModal";
import Menu from "../Conversations/ConversationsBody/components/Menu";
import routes from "routes";
import LockedFeature from "components/Conversations/UserInfoBox/LockedFeature";
import BackButton from "components/BackButton";

const getSelectedConversation = ({ chatsData }) =>
  chatsData.selectedConversation;
const getContainer = ({ userData }) => userData.container.data;

const pagesPathNames = {
  [routes.convsList]: "convsListPage",
  [routes.users]: "onlineUsersPage",
  [routes.agents]: "agentsPage",
  [routes.chats]: "chatsPage",
};

function MainHeader({ toggleUserInfo, toggleSettings }) {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showRoadmapModal, setShowRoadmapModal] = useState(false);
  const dispatch = useDispatch();
  const selectedConversation = useSelector(getSelectedConversation);
  const container = useSelector(getContainer);
  const { getTranslation } = useTranslation([
    "CHAT",
    "COMMON",
    "USERS",
    "AGENSTS",
  ]);
  const { t } = getTranslation();
  const location = useLocation();
  const isSmallDevice = useMediaQuery("(max-width: 1090px)");
  const navigate = useNavigate();

  const convType = selectedConversation?.conversation?.convType;

  const pagesHeaderTitles = {
    onlineUsersPage: t("USERS_LIST_TITLE"),
    agentsPage: t("AGENT_LIST_TITLE"),
  };

  const getCurrentPage = () => {
    for (let route of Object.keys(pagesPathNames)) {
      if (matchPath({ path: route, end: false }, location.pathname)) {
        return pagesPathNames[route];
      }
    }
  };

  const currentPage = getCurrentPage();

  const hideMainHeader = currentPage === "convsListPage" && !!isSmallDevice;

  const showToggleUserInfoMenuBtn = isSmallDevice;

  const { userRoadmapEnabled } = container;

  const handleLogoutBtnClick = (e) => {
    e?.stopPropagation();
    setShowLogoutModal(true);
  };

  const renderLogoutBtnInThreedotsOption = (
    <span className={styles.threedot_logout_option}>
      <ExitIconComponent className={styles.threedot_logout_option__icon} />
      <span>{t("LOGOUT")}</span>
    </span>
  );

  const threeDotsMenuOptions = [
    currentPage === "chatsPage" && {
      text: t("USER_INFO"),
      handler: toggleUserInfo,
    },
    {
      text: t("SETTINGS"),
      handler: toggleSettings,
    },
    {
      text: renderLogoutBtnInThreedotsOption,
      handler: handleLogoutBtnClick,
    },
  ].filter(Boolean);

  const handleUserRoadmapClick = () => {
    if (!userRoadmapEnabled) return;

    dispatch(
      fetchUserRoadmap(selectedConversation?.user?.id, setShowRoadmapModal)
    );
    // setShowRoadmapModal(true);
  };

  const handleLogoutUser = async () => {
    dispatch(logoutSocket({ navigate }));
  };

  const renderLogoutModal = () => {
    return (
      <Modal
        setVisibility={setShowLogoutModal}
        visible={showLogoutModal}
        className="confirmation-modal"
      >
        <ModalBody>
          <div>
            <div className="conv__body__header_block_user_modal_title">
              {t("MODAL").LOGOUT_CONFIRMATION}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="modal__container__footer__left">
            <button
              onClick={() => setShowLogoutModal(false)}
              className="btn btn-tertiary"
              id="closeModal"
            >
              {t("CANCEL")}
            </button>
            <button
              onClick={() => {
                handleLogoutUser();
                setShowLogoutModal(false);
              }}
              className="btn btn-primary"
            >
              {t("CONFIRM")}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    );
  };

  const renderMainHeaderRightSection = () => {
    const showForwardMsg = ["pending", "ongoing"].includes(convType);

    // main header right section's content when one conversation is selected
    if (!!selectedConversation) {
      return (
        <>
          {showForwardMsg && (
            <div className={styles.forward_to_operator_wrapper}>
              <ForwardToOperator />
            </div>
          )}
          <div className={styles.accept_reject_wrapper}>
            <AcceptRejectConv />
          </div>
        </>
      );
    }

    const isInAgentOrOnlineUsersPage = [
      "onlineUsersPage",
      "agentsPage",
    ].includes(currentPage);

    if (isInAgentOrOnlineUsersPage) {
      return (
        <div className="flex flex-ai-c flex-jc-sb w-100">
          <h1 className={styles.page_title}>
            {pagesHeaderTitles[currentPage]}
          </h1>
        </div>
      );
    }

    return null;
  };

  const renderThreeDotsMenu = showToggleUserInfoMenuBtn && (
    <Menu
      options={threeDotsMenuOptions}
      side="right"
      alwaysVisible={true}
      classNames={{ body: styles.threedot_menu_body }}
    />
  );

  const renderNavigationIcons = isSmallDevice ? (
    renderThreeDotsMenu
  ) : (
    <>
      <a
        rel="noreferrer"
        target="_blank"
        href={RAYCHAT_DASHBOARD_URL}
        className="outline_grey_btn dashboard_login_btn"
      >
        <span>{t("LOGIN_TO_DASHBOARD")}</span>
      </a>
      <div
        className="outline_grey_btn logout_btn"
        onClick={handleLogoutBtnClick}
      >
        <ExitIconComponent />
        <span className="">{t("LOGOUT")}</span>
      </div>
    </>
  );

  return (
    <>
      {/* modals */}
      {showLogoutModal && renderLogoutModal()}
      {showRoadmapModal && (
        <RoadmapModal
          setShowRoadmapModal={setShowRoadmapModal}
          showRoadmapModal={showRoadmapModal}
        />
      )}

      {/* header */}
      <div
        className={styles["main-header-wrapper"]}
        id="main-header-wrapper"
        data-hide={hideMainHeader}
      >
        <div className={styles["main-header"]}>
          <div className={styles.right_section}>
            <BackButton />
            {renderMainHeaderRightSection()}
          </div>

          <div className={styles.left_section}>
            <div className="conv__userinfo__options">
              {!!selectedConversation && (
                <>
                  <button
                    disabled={!userRoadmapEnabled}
                    onClick={handleUserRoadmapClick}
                    className={clsx(["outline_grey_btn", classes.roadmap_btn])}
                  >
                    {!userRoadmapEnabled && (
                      <img
                        className={styles.lock_icon}
                        src={filledLockIcon}
                        alt="lock"
                      />
                    )}

                    {!!userRoadmapEnabled && (
                      <RoadmapIcon className="roadmap_header_mobile_menu_item" />
                    )}

                    <span className="flex header_desktop_menu_item">
                      {t("ROADMAP")}
                    </span>
                    {!userRoadmapEnabled && (
                      <LockedFeature
                        classNames={{ notif: classes.disable_tooltip }}
                        showOnlyInfoBadge
                      />
                    )}
                  </button>
                </>
              )}

              {/* we have navigation in all routes that header should get displayed */}
              {renderNavigationIcons}
            </div>
          </div>
        </div>
      </div>

      {/* User information detail */}
    </>
  );
}

export default MainHeader;
